import { getNames, getCodes } from 'country-list';

/**
 * Countries data service using country-list library
 * Provides comprehensive country information including ISO codes and names
 * Uses ISO3 codes for better international compatibility
 * 
 * Based on: https://www.npmjs.com/package/country-list
 */

/**
 * Convert ISO2 to ISO3 code (internal function)
 * @param {string} iso2Code - ISO2 country code (e.g., 'PH', 'US')
 * @returns {string} ISO3 country code (e.g., 'PHL', 'USA')
 */
const convertIso2ToIso3 = (iso2Code) => {
  const iso2ToIso3Map = {
    'AF': 'AFG', 'AL': 'ALB', 'DZ': 'DZA', 'AS': 'ASM', 'AD': 'AND', 'AO': 'AGO', 'AI': 'AIA', 'AQ': 'ATA',
    'AG': 'ATG', 'AR': 'ARG', 'AM': 'ARM', 'AW': 'ABW', 'AU': 'AUS', 'AT': 'AUT', 'AZ': 'AZE', 'BS': 'BHS',
    'BH': 'BHR', 'BD': 'BGD', 'BB': 'BRB', 'BY': 'BLR', 'BE': 'BEL', 'BZ': 'BLZ', 'BJ': 'BEN', 'BM': 'BMU',
    'BT': 'BTN', 'BO': 'BOL', 'BA': 'BIH', 'BW': 'BWA', 'BV': 'BVT', 'BR': 'BRA', 'IO': 'IOT', 'BN': 'BRN',
    'BG': 'BGR', 'BF': 'BFA', 'BI': 'BDI', 'KH': 'KHM', 'CM': 'CMR', 'CA': 'CAN', 'CV': 'CPV', 'KY': 'CYM',
    'CF': 'CAF', 'TD': 'TCD', 'CL': 'CHL', 'CN': 'CHN', 'CX': 'CXR', 'CC': 'CCK', 'CO': 'COL', 'KM': 'COM',
    'CG': 'COG', 'CD': 'COD', 'CK': 'COK', 'CR': 'CRI', 'CI': 'CIV', 'HR': 'HRV', 'CU': 'CUB', 'CY': 'CYP',
    'CZ': 'CZE', 'DK': 'DNK', 'DJ': 'DJI', 'DM': 'DMA', 'DO': 'DOM', 'EC': 'ECU', 'EG': 'EGY', 'SV': 'SLV',
    'GQ': 'GNQ', 'ER': 'ERI', 'EE': 'EST', 'ET': 'ETH', 'FK': 'FLK', 'FO': 'FRO', 'FJ': 'FJI', 'FI': 'FIN',
    'FR': 'FRA', 'GF': 'GUF', 'PF': 'PYF', 'TF': 'ATF', 'GA': 'GAB', 'GM': 'GMB', 'GE': 'GEO', 'DE': 'DEU',
    'GH': 'GHA', 'GI': 'GIB', 'GR': 'GRC', 'GL': 'GRL', 'GD': 'GRD', 'GP': 'GLP', 'GU': 'GUM', 'GT': 'GTM',
    'GG': 'GGY', 'GN': 'GIN', 'GW': 'GNB', 'GY': 'GUY', 'HT': 'HTI', 'HM': 'HMD', 'VA': 'VAT', 'HN': 'HND',
    'HK': 'HKG', 'HU': 'HUN', 'IS': 'ISL', 'IN': 'IND', 'ID': 'IDN', 'IR': 'IRN', 'IQ': 'IRQ', 'IE': 'IRL',
    'IM': 'IMN', 'IL': 'ISR', 'IT': 'ITA', 'JM': 'JAM', 'JP': 'JPN', 'JE': 'JEY', 'JO': 'JOR', 'KZ': 'KAZ',
    'KE': 'KEN', 'KI': 'KIR', 'KP': 'PRK', 'KR': 'KOR', 'KW': 'KWT', 'KG': 'KGZ', 'LA': 'LAO', 'LV': 'LVA',
    'LB': 'LBN', 'LS': 'LSO', 'LR': 'LBR', 'LY': 'LBY', 'LI': 'LIE', 'LT': 'LTU', 'LU': 'LUX', 'MO': 'MAC',
    'MK': 'MKD', 'MG': 'MDG', 'MW': 'MWI', 'MY': 'MYS', 'MV': 'MDV', 'ML': 'MLI', 'MT': 'MLT', 'MH': 'MHL',
    'MQ': 'MTQ', 'MR': 'MRT', 'MU': 'MUS', 'YT': 'MYT', 'MX': 'MEX', 'FM': 'FSM', 'MD': 'MDA', 'MC': 'MCO',
    'MN': 'MNG', 'ME': 'MNE', 'MS': 'MSR', 'MA': 'MAR', 'MZ': 'MOZ', 'MM': 'MMR', 'NA': 'NAM', 'NR': 'NRU',
    'NP': 'NPL', 'NL': 'NLD', 'NC': 'NCL', 'NZ': 'NZL', 'NI': 'NIC', 'NE': 'NER', 'NG': 'NGA', 'NU': 'NIU',
    'NF': 'NFK', 'MP': 'MNP', 'NO': 'NOR', 'OM': 'OMN', 'PK': 'PAK', 'PW': 'PLW', 'PS': 'PSE', 'PA': 'PAN',
    'PG': 'PNG', 'PY': 'PRY', 'PE': 'PER', 'PH': 'PHL', 'PN': 'PCN', 'PL': 'POL', 'PT': 'PRT', 'PR': 'PRI',
    'QA': 'QAT', 'RE': 'REU', 'RO': 'ROU', 'RU': 'RUS', 'RW': 'RWA', 'SH': 'SHN', 'KN': 'KNA', 'LC': 'LCA',
    'PM': 'SPM', 'VC': 'VCT', 'WS': 'WSM', 'SM': 'SMR', 'ST': 'STP', 'SA': 'SAU', 'SN': 'SEN', 'RS': 'SRB',
    'SC': 'SYC', 'SL': 'SLE', 'SG': 'SGP', 'SK': 'SVK', 'SI': 'SVN', 'SB': 'SLB', 'SO': 'SOM', 'ZA': 'ZAF',
    'GS': 'SGS', 'ES': 'ESP', 'LK': 'LKA', 'SD': 'SDN', 'SR': 'SUR', 'SJ': 'SJM', 'SZ': 'SWZ', 'SE': 'SWE',
    'CH': 'CHE', 'SY': 'SYR', 'TW': 'TWN', 'TJ': 'TJK', 'TZ': 'TZA', 'TH': 'THA', 'TL': 'TLS', 'TG': 'TGO',
    'TK': 'TKL', 'TO': 'TON', 'TT': 'TTO', 'TN': 'TUN', 'TR': 'TUR', 'TM': 'TKM', 'TC': 'TCA', 'TV': 'TUV',
    'UG': 'UGA', 'UA': 'UKR', 'AE': 'ARE', 'GB': 'GBR', 'US': 'USA', 'UM': 'UMI', 'UY': 'URY', 'UZ': 'UZB',
    'VU': 'VUT', 'VE': 'VEN', 'VN': 'VNM', 'VG': 'VGB', 'VI': 'VIR', 'WF': 'WLF', 'EH': 'ESH', 'YE': 'YEM',
    'ZM': 'ZMB', 'ZW': 'ZWE'
  };
  return iso2ToIso3Map[iso2Code.toUpperCase()] || iso2Code.toUpperCase();
};

/**
 * Get all countries with enhanced data
 * @returns {Array} Array of country objects with code, name, and additional info
 */
export const getAllCountries = () => {
  const countryNames = getNames();
  const countryCodes = getCodes();
  
  return countryNames.map((name, index) => {
    const iso2Code = countryCodes[index];
    const iso3Code = convertIso2ToIso3(iso2Code);
    
    return {
      code: iso3Code, // ISO3 code
      iso2: iso2Code, // ISO2 code
      name: name,
      // Additional data can be added here if needed
    };
  });
};

/**
 * Get countries formatted for dropdown options
 * @returns {Array} Array of country objects with value and label for dropdown
 *
 * Standard practice: Sorting alphabetically is recommended for usability.
 * Philippines is not moved to the top; all countries are sorted by name.
 */
export const getCountryOptions = () => {
  const countries = getAllCountries();

  // Sort countries alphabetically by name
  const sortedCountries = countries.sort((a, b) => a.name.localeCompare(b.name));

  return sortedCountries.map(country => ({
    value: country.code, // ISO3 code
    label: country.name
  }));
};

/**
 * Get country by ISO3 code
 * @param {string} code - ISO3 country code (e.g., 'PHL', 'USA')
 * @returns {Object|null} Country object or null if not found
 */
export const getCountryByCode = (code) => {
  if (!code) return null;
  
  const countries = getAllCountries();
  return countries.find(country => country.code === code.toUpperCase()) || null;
};

/**
 * Get country by ISO2 code (for backward compatibility)
 * @param {string} code - ISO2 country code (e.g., 'PH', 'US')
 * @returns {Object|null} Country object or null if not found
 */
export const getCountryByIso2 = (code) => {
  if (!code) return null;
  
  const countries = getAllCountries();
  return countries.find(country => country.iso2 === code.toUpperCase()) || null;
};

/**
 * Get a human-friendly country name from various inputs (ISO3, ISO2, or raw name)
 * @param {string} codeOrName - ISO3/ISO2 code or a country name
 * @returns {string} Display name if resolvable, otherwise the input value
 */
export const getCountryName = (codeOrName) => {
  if (!codeOrName) return '-';
  const value = String(codeOrName).trim();
  // Try ISO3
  const byIso3 = getCountryByCode(value);
  if (byIso3?.name) return byIso3.name;
  // Try ISO2
  const byIso2 = getCountryByIso2(value);
  if (byIso2?.name) return byIso2.name;
  // Fallback to raw value
  return value;
};

/**
 * Search countries by name or code
 * @param {string} query - Search query
 * @returns {Array} Array of matching countries
 */
export const searchCountries = (query) => {
  if (!query || query.length < 2) return [];
  
  const countries = getAllCountries();
  const lowercaseQuery = query.toLowerCase();
  
  return countries.filter(country => 
    country.name.toLowerCase().includes(lowercaseQuery) ||
    country.code.toLowerCase().includes(lowercaseQuery) ||
    country.iso2.toLowerCase().includes(lowercaseQuery)
  );
};

/**
 * Get countries by region/continent
 * @param {string} region - Region name (e.g., 'Asia', 'Europe')
 * @returns {Array} Array of countries in the specified region
 */
export const getCountriesByRegion = (region) => {
  // This would need additional data to work properly
  // For now, return all countries
  return getAllCountries();
};

/**
 * Get default country (Philippines)
 * @returns {Object} Default country object
 */
export const getDefaultCountry = () => {
  return getCountryByCode('PHL') || {
    code: 'PHL', // ISO3 code
    iso2: 'PH',  // ISO2 code
    name: 'Philippines'
  };
};

/**
 * Convert ISO2 to ISO3 code
 * @param {string} iso2Code - ISO2 country code
 * @returns {string|null} ISO3 country code or null if not found
 */
export const iso2ToIso3 = (iso2Code) => {
  if (!iso2Code) return null;
  return convertIso2ToIso3(iso2Code.toUpperCase());
};

/**
 * Convert ISO3 to ISO2 code
 * @param {string} iso3Code - ISO3 country code
 * @returns {string|null} ISO2 country code or null if not found
 */
export const iso3ToIso2 = (iso3Code) => {
  if (!iso3Code) return null;
  
  const countries = getAllCountries();
  const country = countries.find(c => c.code === iso3Code.toUpperCase());
  return country ? country.iso2 : null;
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use getCountryOptions() instead
 */
export const COUNTRIES = getCountryOptions().map(option => ({
  code: option.value, // ISO3 code
  name: option.label
}));
